import { Button, Icon } from "@elit-hair/elit-ui";
import { TbX } from "react-icons/tb";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

import { NOTIFICATION_VARIANT_CONFIG, NotificationVariant } from "./notification-variants";

type NotificationToastVariant = NotificationVariant;

type NotificationToastMeta = {
    label: string;
    value: string;
};

type NotificationToastCardProps = {
    variant: NotificationToastVariant;
    title: string;
    description?: string;
    meta?: NotificationToastMeta[];
    action?: {
        label: string;
        onClick: () => void;
    };
    onDismiss: () => void;
};

export const NotificationToastCard = ({
    variant,
    title,
    description,
    meta,
    action,
    onDismiss,
}: NotificationToastCardProps) => {
    const config = NOTIFICATION_VARIANT_CONFIG[variant];

    return (
        <div
            className={cn(
                "relative flex w-full gap-3 rounded-xl border border-border bg-background p-4 shadow-lg",
                "border-l-4",
                config.accent
            )}
        >
            <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", config.iconBg)}>
                <Icon icon={config.icon} className={config.iconColor} size={18} />
            </div>

            <div className="min-w-0 flex-1">
                <p className="pr-6 text-sm font-semibold leading-snug text-foreground">{title}</p>
                {description && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{description}</p>}
                {meta && meta.length > 0 && (
                    <dl className="mt-2 space-y-0.5">
                        {meta.map((item) => (
                            <div key={item.label} className="flex gap-1.5 text-xs">
                                <dt className="text-muted-foreground">{item.label}:</dt>
                                <dd className="font-medium text-foreground/90">{item.value}</dd>
                            </div>
                        ))}
                    </dl>
                )}
                {action && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 h-8"
                        onClick={() => {
                            action.onClick();
                            onDismiss();
                        }}
                    >
                        {action.label}
                    </Button>
                )}
            </div>

            <button
                type="button"
                onClick={onDismiss}
                className="absolute right-3 top-3 rounded-md p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Dismiss"
            >
                <TbX size={16} />
            </button>
        </div>
    );
};

type ShowNotificationToastOptions = Omit<NotificationToastCardProps, "onDismiss"> & {
    duration?: number;
};

export const showNotificationToast = ({ duration = 6000, ...props }: ShowNotificationToastOptions) => {
    toast.custom((id) => <NotificationToastCard {...props} onDismiss={() => toast.dismiss(id)} />, {
        position: "top-center",
        duration,
        unstyled: true,
        classNames: {
            toast: "w-full max-w-md p-0 bg-transparent border-0 shadow-none",
        },
    });
};
